// In-app notifications for volunteers — the "you're on deck" banner.
//
// There was no toast, banner or alert system anywhere in this codebase before this: every
// piece of feedback in the volunteer UI is a blocking window.alert(). That is fine for "are
// you sure?" and useless for "you're up in a minute", so this is new construction.
//
// ─── What this can and cannot do ────────────────────────────────────────────────
// There is no service worker and no push infrastructure, so none of this reaches a locked
// phone or a browser that has been swiped away. It reaches a device that is awake with the
// page open. That limit is why the backstage page exists — something you prop up and leave
// running — and why the banner persists instead of flashing past.

'use strict';

const esc = (s) => String(s == null ? '' : s)
	.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const SOUND_KEY = 'vol-ondeck-sound';

// ─── attention ───────────────────────────────────────────────────────────────
// Each of these is best-effort and silent on failure. A browser that refuses one must not
// stop the others, and none of them may throw into the socket handler that called them.

function vibrate() {
	try {
		if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
	} catch (e) { /* unsupported or blocked by a permissions policy */ }
}

/**
 * A two-note chime, synthesised rather than loaded — no asset, no pipeline change, nothing
 * to 404. Off unless the user turned it on, because autoplay policy needs a prior gesture
 * and a page that silently fails to beep is worse than one that never promised to.
 */
function chime() {
	try {
		if (window.localStorage.getItem(SOUND_KEY) !== 'on') return;

		const Ctx = window.AudioContext || window.webkitAudioContext;
		if (!Ctx) return;

		const ctx = new Ctx();
		[[880, 0], [1174, 0.16]].forEach(([freq, at]) => {
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();
			osc.type = 'sine';
			osc.frequency.value = freq;
			gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
			gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
			gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35);
			osc.connect(gain).connect(ctx.destination);
			osc.start(ctx.currentTime + at);
			osc.stop(ctx.currentTime + at + 0.4);
		});

		setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
	} catch (e) { /* no audio context, or blocked */ }
}

/**
 * Flash the tab title, for someone who has switched to another tab. Stops on focus so a
 * person returning to the page does not find it still blinking at them.
 */
function flashTitle(text) {
	const original = document.title;
	let on = false;

	const timer = setInterval(() => {
		on = !on;
		document.title = on ? text : original;
	}, 900);

	const stop = () => {
		clearInterval(timer);
		document.title = original;
		window.removeEventListener('focus', stop);
	};

	window.addEventListener('focus', stop);
	setTimeout(stop, 30000);
}

// ─── the banner ──────────────────────────────────────────────────────────────

/**
 * @param root      element the banner is rendered into
 * @param onDismiss called with a notification id when the user clears it, so the caller can
 *                  acknowledge it server-side and stop it reappearing on the next load
 */
function createBanner(root, onDismiss) {
	if (!root) return {show: () => {}, clear: () => {}};

	// Rendered fresh each time rather than bound per-element: this container is rebuilt with
	// innerHTML, and per-element listeners here would accumulate one handler per render.
	root.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-dismiss-notification]');
		if (!btn) return;

		const id = btn.getAttribute('data-dismiss-notification');
		const card = btn.closest('.vol-ondeck');
		if (card) card.remove();
		if (onDismiss) onDismiss(id);
	});

	const render = (notification) => {
		const el = document.createElement('div');
		el.className = 'vol-ondeck';
		el.setAttribute('role', 'alert');
		el.setAttribute('data-notification-id', notification._id);

		el.innerHTML =
			'<div class="vol-ondeck-mark" aria-hidden="true">!</div>' +
			'<div class="vol-ondeck-text">' +
				'<strong class="vol-ondeck-title">' + esc(notification.title) + '</strong>' +
				'<span class="vol-ondeck-body">' + esc(notification.body) + '</span>' +
			'</div>' +
			'<button type="button" class="vol-ondeck-close" aria-label="Dismiss" ' +
				'data-dismiss-notification="' + esc(notification._id) + '">&times;</button>';

		return el;
	};

	return {
		/**
		 * @param loud false when replaying something they may already have seen (a page load
		 *             or reconnect) — the banner still appears, but without the fanfare.
		 */
		show(notification, loud) {
			if (!notification || !notification._id) return;
			if (root.querySelector('[data-notification-id="' + notification._id + '"]')) return;

			root.appendChild(render(notification));

			if (loud) {
				vibrate();
				chime();
				if (document.hidden) flashTitle('★ ' + notification.title);
			}
		},

		clear() { root.innerHTML = ''; },
	};
}

/**
 * The sound opt-in. A checkbox rather than an implicit "we'll beep once you interact",
 * because a chime in a quiet green room during a performance needs to be a decision.
 */
function bindSoundToggle(el) {
	if (!el) return;

	el.checked = window.localStorage.getItem(SOUND_KEY) === 'on';
	el.addEventListener('change', () => {
		window.localStorage.setItem(SOUND_KEY, el.checked ? 'on' : 'off');
		// Confirm audibly that it works — this click is also the gesture that unlocks audio.
		if (el.checked) chime();
	});
}

module.exports = {createBanner, bindSoundToggle, vibrate, chime, flashTitle};
